import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

const TOKEN_REFRESHED_EVENT = 'periverse:token-refreshed';

/**
 * Delay before invalidating to let SSE connections close/reconnect first.
 * Prevents query refetch burst from queuing behind SSE streams (connection limit).
 */
const INVALIDATE_DELAY_MS = 150;

/**
 * Listens for token refresh events and invalidates React Query cache
 * so active queries refetch with the new token.
 * Fixes "API calls on pause" when session expires while user stays in tab.
 * Uses a short delay so SSE reconnections can complete before the refetch burst.
 */
export function TokenRefreshInvalidation() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleTokenRefreshed = () => {
      const timeoutId = window.setTimeout(() => {
        queryClient.invalidateQueries();
      }, INVALIDATE_DELAY_MS);
      return () => window.clearTimeout(timeoutId);
    };

    let cleanup: (() => void) | undefined;
    const handler = () => {
      cleanup?.();
      cleanup = handleTokenRefreshed();
    };

    window.addEventListener(TOKEN_REFRESHED_EVENT, handler);
    return () => {
      window.removeEventListener(TOKEN_REFRESHED_EVENT, handler);
      cleanup?.();
    };
  }, [queryClient]);

  return null;
}
